<template>
  <FloatLabel :variant="variant" class="relative">
    <IconField>
      <!-- Leading Icon on the input -->
      <InputIcon v-if="icon">
        <Icon :icon="icon" :class="[iconColor ? iconColor : null, disabled ? 'cursor-not-allowed opacity-70': null]"/>
      </InputIcon>

      <InputText
        type="text"
        v-model="value"
        @input="emit('update:modelValue', $event.target.value?.trim().length ? $event.target.value : null)"
        @focus="emit('focus', $event), inputFocused = true"
        @blur="emit('blur', $event), inputFocused = false"
        :disabled="disabled"
        :placeholder="placeholder"
        :size="size"
        :title="title"
        :tabindex="Number(tabindex)"
        :maxlength="Number(maxlength)"
        :pt="{
          root: ({ context }) => ({
            class: [
              // Size
              'w-full',

              // Color and Background
              'focus:border-iris-600',
              'focus:ring-iris-300/40 dark:focus:ring-iris-400/40',
              'placeholder:text-charcoal-700 dark:placeholder:text-charcoal-400',

              { 'focus:ring-2': !minimal },

              // Minimal State
              { 'border-b shadow-none rounded-none border-t-0 border-l-0 border-r-0 focus:ring-0 bg-transparent' : minimal },

              // Disabled State
              { 'cursor-not-allowed pointer-events-auto opacity-70': context.disabled },
              { 'bg-transparent' : context.disabled && minimal },
              { ' bg-white dark:bg-charcoal-900' : context.disabled && !minimal },

              // Setting Changed and Error border and ring colors
              setInputStateBorderAndRingColor(),

              // Misc
              'transition-none'
            ]
          }),
        }"
      />

      <!-- Float Label label -->
      <label v-if="label">{{ label }}</label>

      <!-- Cancel Icon -->
      <InputIcon v-if="nullable && value">
        <Icon
          icon="heroicons:x-mark"
          @click="emit('update:modelValue', null)"
          class="cursor-pointer hover:text-charcoal-600 dark:hover:text-charcoal-200"
        />
      </InputIcon>
    </IconField>
  </FloatLabel>
</template>

<script setup>
import FloatLabel from 'primevue/floatlabel'
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import { Icon } from '@iconify/vue'
import { ref } from "vue";

const emit = defineEmits(['blur', 'focus', 'update:modelValue'])

const props = defineProps({
  // Setting the Yellow border/ring around the input
  changed: {
    type: Boolean,
  },
  disabled: {
    type: Boolean
  },
  // Setting the Red border/ring around the input
  error: {
    type: Boolean
  },
  icon: {
    type: String
  },
  iconColor: {
    type: String
  },
  // Used in conjunction with variant prop for Float Label
  label: {
    type: String
  },
  // Max character length of input
  maxlength: {
    type: [String, Number]
  },
  // For a border bottom instead of entire border
  minimal: {
    type: Boolean
  },
  // Show the X and allow to be cleared out
  nullable: {
    type: Boolean
  },
  // If not using Float Label
  placeholder: {
    type: String
  },
  // Controls the padding and text size of the input
  size: {
    type: String,
    validator: (val) => ['small', 'large'].includes(val),
    default: null
  },
  // Title for the Input
  tabindex: {
    type: [String, Number],
    default: null
  },
  // Title for the Input
  title: {
    type: String
  },
  // Used for the Float Label
  variant: {
    type: String,
    validator: (val) => ['in', 'on', 'over'].includes(val),
    default: null
  },
})

const value = defineModel()
const inputFocused = ref(false)

function setInputStateBorderAndRingColor() {
  if (!inputFocused.value) {
    // If minimal input styling
    if (props.minimal) {
      if (props.error) {
        return 'border-red-500 ring-red-500/40'
      }
      else if (props.changed) {
        return 'border-yellow-500 ring-yellow-500/40'
      }
    }
    // If input has errors styling
    else if (props.error) {
      return 'border-red-500 ring-2 ring-red-500/40'
    }
    // If input is changed styling
    else if (props.changed) {
      return 'border-yellow-500 ring-2 ring-yellow-500/40'
    }

    // Default border for when input is not focused
    return 'border-charcoal-200 dark:border-charcoal-800'
  }
}
</script>